'use client';

import { useCallback, useMemo, type ReactNode } from 'react';
import type {
  EndpointDetectionFindingDto,
  EndpointDetectionSignalDto,
} from '@terra/shared-contracts-endpoints';
import { AssetCellHoverCard } from './AssetCellHoverCard';
import { EndpointDetectionsHoverCard } from './EndpointDetectionsHoverCard';
import { sortDetectionsByActiveFirst } from './utils/endpoint-detections.utils';
import type { DetectionView } from './types';
import { DETECTION_VIEW } from './types';

const HOVER_CARD_WIDTH = '612px';

export type DetectionsHoverCardPopoverProps = {
  type: 'finding' | 'signal';
  detectionView: DetectionView;
  findings: EndpointDetectionFindingDto[];
  signals: EndpointDetectionSignalDto[];
  isLoading: boolean;
  isError: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  onViewFinding?: (findingId: string) => void;
  onViewSignal?: (signalId: string) => void;
};

export const DetectionsHoverCardPopover = ({
  type,
  detectionView,
  findings,
  signals,
  isLoading,
  isError,
  isOpen,
  onOpenChange,
  children,
  onViewFinding,
  onViewSignal,
}: DetectionsHoverCardPopoverProps) => {
  const visibleFindings = useMemo(() => {
    const sortedFindings = sortDetectionsByActiveFirst({ items: findings });

    return detectionView === DETECTION_VIEW.ALL
      ? sortedFindings
      : sortedFindings.filter((finding) => finding.isActive);
  }, [detectionView, findings]);

  const visibleSignals = useMemo(() => {
    const sortedSignals = sortDetectionsByActiveFirst({ items: signals });

    return detectionView === DETECTION_VIEW.ALL
      ? sortedSignals
      : sortedSignals.filter((signal) => signal.isActive);
  }, [detectionView, signals]);

  const handleFindingSelect = useCallback(
    (findingId: string) => {
      onOpenChange(false);
      queueMicrotask(() => onViewFinding?.(findingId));
    },
    [onOpenChange, onViewFinding],
  );

  const handleSignalSelect = useCallback(
    (signalId: string) => {
      onOpenChange(false);
      queueMicrotask(() => onViewSignal?.(signalId));
    },
    [onOpenChange, onViewSignal],
  );

  return (
    <AssetCellHoverCard
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      trigger={children}
      width={HOVER_CARD_WIDTH}
      placement="bottom-end"
    >
      <EndpointDetectionsHoverCard
        type={type}
        findings={visibleFindings}
        signals={visibleSignals}
        isLoading={isLoading}
        isError={isError}
        onFindingSelect={onViewFinding ? handleFindingSelect : undefined}
        onSignalSelect={onViewSignal ? handleSignalSelect : undefined}
      />
    </AssetCellHoverCard>
  );
};
